<template>
    <div class="page">
        <kt-datatable
            :model="workOrderModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: props.embedded ? '-table-bordered table-sm' : '-table-bordered', rowClass: (row) => getRowClass(row) }"
            :update-function="handleDatatableUpdate"
            :hide-search="props.embedded"
            :storage-key="props.embedded ? 'requests-embedded' : 'requests-list'"
            @cell-click="handleCellClick"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>
            
            <template #view_actions="scope">
                <kt-dropmenu v-model:visible="actionDropdown['row_' + scope.row.Id]">
                    <template #button>
                        <button
                            class="btn btn-outline-primary btn-sm"
                            @click="actionDropdown['row_' + scope.row.Id] = !actionDropdown['row_' + scope.row.Id]"
                        >
                            <i class="fas fa-ellipsis-v"></i>
                        </button>
                    </template>
                    <template #dropmenu>
                        <ul class="dropdown-menu show">
                            <li>
                                <label class="dropdown-item">
                                    <button 
                                        class="btn btn-sm btn-outline-primary form-control" 
                                        @click="editRecord(scope.row.Id)"
                                    >
                                        <i class="fa fa-edit"></i> Edit
                                    </button>
                                </label>
                            </li>
                            <li>
                                <label class="dropdown-item">
                                    <button 
                                        class="btn btn-sm btn-outline-danger form-control" 
                                        @click="deleteRecord(scope.row.Id)"
                                    >
                                        <i class="fa fa-trash"></i> Delete
                                    </button>
                                </label>
                            </li>
                        </ul>
                    </template>
                </kt-dropmenu>
            </template>

            <template #filters="scope" v-if="!props.hideFilters">
                <div class="col">
                    <div class="row">
                        <div class="col-auto">
                            <client-multi-check 
                                label="Clients"
                                :model-value="currentFilter.client"
                                @update:model-value="(value) => setFilter({client: value})"
                            ></client-multi-check>
                        </div>
                        <div class="col-auto">
                            <work-order-status-multi-check
                                label="Status"
                                :model-value="currentFilter.status"
                                @update:model-value="(value) => setFilter({status: value})"
                            ></work-order-status-multi-check>
                        </div>
                        <div class="col-auto">
                            <priority-multi-check
                                label="Priority"
                                :model-value="currentFilter.priority"
                                @update:model-value="(value) => setFilter({priority: value})"
                            ></priority-multi-check>
                        </div>
                        <div class="col-auto">
                            <tech-multi-check
                                label="Technician"
                                :model-value="currentFilter.technician"
                                @update:model-value="(value) => setFilter({technician: value})"
                            ></tech-multi-check>
                        </div>
                        <div class="col-auto">
                            <department-multi-check
                                label="Department"
                                :model-value="currentFilter.department"
                                @update:model-value="(value) => setFilter({department: value})"
                            ></department-multi-check>
                        </div>
                        <div class="col-auto">
                            <kt-date-range-select
                                label="Date Range"
                                v-model="dateRange"
                            ></kt-date-range-select>
                        </div>
                    </div>
                </div>
            </template>

            <template #after-search="scope">
                <div class="-btn-group">
                    <button @click="refresh()" class="btn btn-sm btn-outline-secondary me-2">
                        Refresh <i class="fa fa-sync"></i>
                    </button>
                    <button @click="newRecord()" class="btn btn-sm btn-outline-primary">
                        New <i class="fa fa-plus"></i>
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Toolbar and Filter Badges -->
            <template #under-header>
                <!-- Bulk Actions Toolbar -->
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <button 
                            v-if="hasDeletedRowsSelected"
                            class="btn btn-sm btn-outline-success"
                            @click="confirmBulkRestore"
                        >
                            <RotateCcw :size="14" class="me-1" />
                            Restore
                        </button>
                        <button 
                            v-else
                            class="btn btn-sm btn-outline-danger"
                            @click="confirmBulkDelete"
                        >
                            <Trash2 :size="14" class="me-1" />
                            Delete
                        </button>
                        <button 
                            class="btn btn-sm btn-outline-secondary ms-auto"
                            @click="clearSelection"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
                
                <!-- Filter Badges -->
                <div v-if="hasActiveFilters" class="d-flex flex-wrap gap-2 p-2">
                    <span class="text-muted small me-2">Active filters:</span>
                    
                    <!-- Client badges -->
                    <span v-for="clientId in currentFilter.client" :key="'client-' + clientId"
                          class="badge bg-primary d-flex align-items-center">
                        Client: {{ getClientName(clientId) }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeClientFilter(clientId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Status badges -->
                    <span v-for="statusId in currentFilter.status" :key="'status-' + statusId"
                          class="badge bg-info d-flex align-items-center">
                        Status: {{ getStatusName(statusId) }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeStatusFilter(statusId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Priority badges -->
                    <span v-for="priority in currentFilter.priority" :key="'priority-' + priority"
                          class="badge bg-warning text-dark d-flex align-items-center">
                        Priority: {{ priority }}
                        <button type="button" class="btn-close ms-2" 
                                @click="removePriorityFilter(priority)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Technician badges -->
                    <span v-for="techId in currentFilter.technician" :key="'tech-' + techId"
                          class="badge bg-success d-flex align-items-center">
                        Technician: {{ getTechName(techId) }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeTechnicianFilter(techId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Department badges -->
                    <span v-for="dept in currentFilter.department" :key="'dept-' + dept"
                          class="badge bg-dark d-flex align-items-center">
                        Department: {{ dept }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeDepartmentFilter(dept)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Date range badge -->
                    <span v-if="currentFilter.startDate || currentFilter.endDate"
                          class="badge bg-secondary d-flex align-items-center">
                        Date: {{ formatDateRange() }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeDateFilter()"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Include Deleted badge -->
                    <span v-if="currentFilter.includeDeleted"
                          class="badge bg-danger d-flex align-items-center">
                        Including Deleted
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="setFilter({ includeDeleted: false })"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Clear all filters -->
                    <button class="btn btn-sm btn-outline-secondary ms-0" 
                            @click="clearAllFilters()">
                        Clear All
                    </button>
                </div>
            </template>

            <template #view_string="scope">
                <span v-if="scope.column.name === 'Contact'">
                    <a href="javascript:void(0);" @click="editContact(scope.row.Contact?.Id)">
                        {{ scope.row.Contact?.Company || 'N/A' }}
                    </a>
                </span>
                <span v-else-if="scope.column.name === 'RequestId'">
                    <a v-if="scope.row.RequestId" href="javascript:void(0);" @click="openJobForm(scope.row.RequestId)">
                        {{ scope.row.RequestId }}
                    </a>
                    <span v-else>-</span>
                </span>
                <span v-else-if="scope.column.name === 'RequestTitle'">
                    <strong>{{ scope.row.RequestTitle || '--' }}</strong>
                </span>
                <span v-else-if="scope.column.name === 'Status'">
                    <span :class="getStatusBadgeClass(scope.row.Status)" class="badge">
                        {{ getStatusLabel(scope.row.Status) }}
                    </span>
                </span>
                <span v-else-if="scope.column.name === 'Priority'">
                    <span :class="getPriorityBadgeClass(scope.row.Priority)" class="badge d-inline-flex align-items-center">
                        <component :is="getPriorityIcon(scope.row.Priority)" :size="14" class="me-1" />
                        {{ scope.row.Priority === 'None' ? 'None' : (scope.row.Priority || 'Cancelled') }}
                    </span>
                </span>
                <span v-else-if="scope.column.name === 'Technician'">
                    {{ scope.row.Owner ? getTechName(scope.row.Owner) : '' }}
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
        </kt-datatable>
        
        <!-- Bulk Delete Confirmation Modal -->
        <kt-modal
            :visible="showDeleteModal"
            title="Confirm Bulk Delete"
            @dismiss="showDeleteModal = false"
        >
            <template #body>
                <div class="container-fluid">
                    <p class="text-danger mb-3">
                        <i class="fas fa-exclamation-triangle"></i>
                        Are you sure you want to delete {{ selectedRows.length }} request(s)?
                    </p>
                </div>
            </template>
            <template #footer>
                <div class="kt-modal-actions">
                    <button class="btn btn-outline-secondary" @click="showDeleteModal = false">
                        <X :size="16" class="me-1" />Cancel
                    </button>
                    <button class="btn btn-danger" @click="confirmBulkDelete">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                </div>
            </template>
        </kt-modal>
        
        <!-- Embedded Edit Modal -->
        <request-edit 
            v-if="(props.embedded || props.requestId) && showEditModal" 
            :id="editingRecordId"
            :parent-request-id="props.requestId"
            :client-id="props.clientId"
            :is-new="isNewRecord"
            @close="closeEditModal"
            @saved="onRecordSaved"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import Api from "@/services/ApiService";
import { ApiHttpResponse } from "@/services/Http";
import { WorkOrderModel, WorkOrderFilter } from "@/models/WorkOrderModel";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import Emitter from "@/services/Emitter";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import KtDropmenu from "@/components/UI/KtDropmenu.vue";
import KtModal from "@/components/UI/KtModal.vue";
import { X, Trash2, Circle, Info, AlertTriangle, AlertCircle, RotateCcw, CircleX } from 'lucide-vue-next';
import ClientMultiCheck from "@/components/Clients/ClientMultiCheck.vue";
import WorkOrderStatusMultiCheck from "@/components/WorkOrders/WorkOrderStatusMultiCheck.vue";
import PriorityMultiCheck from "@/components/Requests/PriorityMultiCheck.vue";
import KtDateRangeSelect from "@/components/UI/KtDateRangeSelect.vue";
import RequestEdit from "@/components/Requests/RequestEdit.vue";
import TechMultiCheck from "@/components/Techs/TechMultiCheck.vue";
import DepartmentMultiCheck from "@/components/Jobs/DepartmentMultiCheck.vue";
import Cache from "@/services/Cache";
import { useClientStore } from '@/stores/ClientStore';
import { useTechStore } from '@/stores/TechStore';
import { storeToRefs } from 'pinia';
import { useDatatableSettings } from '@/composables';

const props = defineProps({
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    hideFilters: {
        type: Boolean,
        default: false
    },
    embedded: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['count-loaded']);

const router = useRouter();
const route = useRoute();
const rows = ref<WorkOrderModel[]>([]);
const total = ref(0);
const actionDropdown = ref<Record<string, boolean>>({});

// Load client store for filter badge display
const clientStore = useClientStore();
const { list: ClientList } = storeToRefs(clientStore);
clientStore.reload();

// Load tech store for technician filter badge display
const techStore = useTechStore();
techStore.reload();

// Datatable settings for persisting user preferences
const { 
    settings: datatableSettings, 
    loadSettings: loadDatatableSettings, 
    saveFilters: saveDatatableFilters,
    hasLoaded: settingsLoaded 
} = useDatatableSettings('RequestsDatatable');

const handleCellClick = (row: any, column: any, value: any, rowIndex: number) => {
    if (column.name !== 'Select') {
        editRecord(row.Id);
    } else {
        toggleRowSelection(row.Id);
    }
};

// Filter state interface
interface RequestsListFilter {
    client: number[];
    status: number[];
    priority: string[];
    technician: number[];
    department: string[];
    searchTerm: string;
    startDate: string;
    endDate: string;
    includeDeleted: boolean;
    page: number;
    size: number;
    sort: any[];
}

// Current filter state (reactive ref)
const currentFilter = ref<RequestsListFilter>({
    client: [],
    status: [],
    priority: [],
    technician: [],
    department: [],
    searchTerm: '',
    startDate: '',
    endDate: '',
    includeDeleted: false,
    page: 0,
    size: props.embedded ? 9999 : 25,
    sort: []
});

// Datatable filter for pagination (derived from currentFilter) - matches JobDatatable pattern
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: currentFilter.value.searchTerm,
    sort: currentFilter.value.sort
}));

// Date range picker (derived from currentFilter)
const dateRange = computed({
    get: () => ({
        startDate: currentFilter.value.startDate || null,
        endDate: currentFilter.value.endDate || null
    }),
    set: (value) => {
        setFilter({
            startDate: value?.startDate || null,
            endDate: value?.endDate || null
        });
    }
});

// Selection state
const selectedRows = ref<number[]>([]);
const selectAll = ref(false);
const showDeleteModal = ref(false);

const statusOptions = [
    { label: 'Draft', value: 0 },
    { label: 'On Hold', value: 1 },
    { label: 'Approved', value: 2 },
    { label: 'Complete', value: 3 },
    { label: 'Cancelled', value: 4 },
    { label: 'Finalized', value: 5 },
    { label: 'Assigned', value: -2 },
    { label: 'Deleted', value: -1 }
];

const workOrderModel = [
    {
        name: "Select",
        label: "Select",
        type: "select",
        width: 60,
        locked: true,
        draggable: false
    },
    {
        name: "Id",
        label: "ID",
        display: true,
        sortable: true
    },
    {
        name: "RequestId",
        label: "Work Order",
        display: true,
        sortable: true
    },
    /*
    {
        name: "RequestWorkOrderIds",
        label: "Request WorkOrder IDs",
        display: true,
        type: "string"
    },
    */
    {
        name: "RequestTitle",
        label: "Request Title",
        display: true,
        type: "string",
        sortable: true
    },
    {
        name: "Contact",
        label: "Client",
        display: true,
        type: "string",
        sortable: true
    },
    {
        name: "Priority",
        label: "Priority",
        display: true,
        type: "string",
        sortable: true
    },
    {
        name: "DateCreated",
        label: "Created",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "Status",
        label: "Status",
        display: true,
        type: "string"
    },
    {
        name: "Description",
        label: "Description",
        display: false
    },
    {
        name: "Location",
        label: "Location",
        display: false
    },
    {
        name: "Technician",
        label: "Technician",
        display: true,
        type: "string"
    },
    {
        name: "DepartmentCategory",
        label: "Department",
        display: true,
        type: "string"
    }
];

const handleDatatableUpdate = async (filter: any) => {
    try {
        // Update currentFilter with pagination/sort/search from datatable
        if (filter.page !== undefined) currentFilter.value.page = filter.page;
        if (filter.size !== undefined) currentFilter.value.size = filter.size;
        if (filter.sort !== undefined) currentFilter.value.sort = filter.sort || [];
        if (filter.search !== undefined) currentFilter.value.searchTerm = filter.search;
        
        // Update URL to reflect current filter state (skip in embedded mode or if not on request route)
        const isOnRequestRoute = route.path.startsWith('/request');
        console.debug('RequestsDatatable handleDatatableUpdate - embedded:', props.embedded, 'requestId:', props.requestId, 'isOnRequestRoute:', isOnRequestRoute);
        if (!props.embedded && !props.requestId && isOnRequestRoute) {
            const urlParams = buildUrlFromFilter(currentFilter.value);
            const newUrl = `/request/list${urlParams ? '?' + urlParams : ''}`;
            console.debug('RequestsDatatable - replacing URL to:', newUrl);
            router.replace(newUrl);
            
            // Save to storage
            Cache.set('requests-list-filter', currentFilter.value);
            saveDatatableFilters(currentFilter.value);
        }
        
        const params: WorkOrderFilter = {
            ...filter,
            IncludeContact: true,
            Title: 'Request'  // Filter to only show Requests
        };

        // If requestId prop is provided, filter by that request only
        if (props.requestId) {
            const requestIdNum = typeof props.requestId === 'string' ? parseInt(props.requestId) : props.requestId;
            params.RequestId = [requestIdNum];
        }

        // If clientId prop is provided, filter by that client
        if (props.clientId) {
            const clientIdNum = typeof props.clientId === 'string' ? parseInt(props.clientId) : props.clientId;
            params.ContactId = [clientIdNum];
        }

        // Apply custom filters
        if (currentFilter.value.client && currentFilter.value.client.length > 0) {
            params.ContactId = currentFilter.value.client;
        }

        if (currentFilter.value.status && currentFilter.value.status.length > 0) {
            // Check if "Deleted" status (-1) is selected
            const hasDeletedStatus = currentFilter.value.status.includes(-1);
            if (hasDeletedStatus) {
                params.IncludeDeleted = true;
            }
            // Check if "Assigned" status (-2) is selected
            const hasAssignedStatus = currentFilter.value.status.includes(-2);
            if (hasAssignedStatus) {
                params.Assigned = true;
            }
            // Filter out the -1 (Deleted) and -2 (Assigned) from actual status filter
            const realStatuses = currentFilter.value.status.filter(s => s !== -1 && s !== -2);
            if (realStatuses.length > 0) {
                params.Status = realStatuses;
            }
        }

        if (currentFilter.value.startDate) {
            params.StartDate = new Date(currentFilter.value.startDate);
        }

        if (currentFilter.value.endDate) {
            params.EndDate = new Date(currentFilter.value.endDate);
        }

        if (currentFilter.value.priority && currentFilter.value.priority.length > 0) {
            params.Priority = currentFilter.value.priority;
        }

        if (currentFilter.value.technician && currentFilter.value.technician.length > 0) {
            params.Owner = currentFilter.value.technician;
        }

        if (currentFilter.value.department && currentFilter.value.department.length > 0) {
            params.Department = currentFilter.value.department;
        }

        // Add SearchTerm parameter for server-side search
        if (currentFilter.value.searchTerm) {
            params.SearchTerm = currentFilter.value.searchTerm;
        }

        // Add OrderBy parameter for server-side sorting
        if (currentFilter.value.sort && currentFilter.value.sort.length > 0) {
            const orderByParts = currentFilter.value.sort.map(sortObj => {
                const [column, direction] = Object.entries(sortObj)[0];
                return direction === 'DESC' ? `-${column}` : column;
            });
            params.OrderBy = orderByParts.join(',');
        }

        console.debug("Requests params", params);

        const response: ApiHttpResponse<WorkOrderModel[]> = await Api.loadWorkOrders(params);

        rows.value = response.Data || [];
        total.value = response.Meta?.Total || 0;
        
        // Emit count for parent components (e.g., JobForm tabs)
        emit('count-loaded', rows.value.length);
        
        return {
            data: rows.value,
            total: total.value
        };
    } catch (error) {
        console.error('Failed to load requests:', error);
        Alert.notify({ text: 'Failed to load requests', type: 'error' });
        return { data: [], total: 0 };
    }
};

// Parse sort parameter from URL string to KtDatatable sort array format  
const parseSortFromUrl = (sortParam?: string): Array<Record<string, 'ASC' | 'DESC'>> => {
    if (!sortParam) return [];
    
    return sortParam.split(',').map(sortField => {
        const trimmed = sortField.trim();
        if (trimmed.startsWith('-')) {
            const column = trimmed.substring(1);
            return { [column]: 'DESC' as const };
        } else {
            return { [trimmed]: 'ASC' as const };
        }
    });
};

// Parse URL params to filter object
const parseUrlToFilter = (): RequestsListFilter => {
    const routeQuery = route.query;
    
    const clientArray = routeQuery.client ? String(routeQuery.client).split(',').map(Number).filter(n => !isNaN(n)) : [];
    const statusArray = routeQuery.status ? String(routeQuery.status).split(',').map(Number).filter(n => !isNaN(n)) : [];
    const priorityArray = routeQuery.priority ? String(routeQuery.priority).split(',').filter(s => s) : [];
    
    const technicianArray = routeQuery.technician ? String(routeQuery.technician).split(',').map(Number).filter(n => !isNaN(n)) : [];
    const departmentArray = routeQuery.department ? String(routeQuery.department).split(',').filter(s => s) : [];
    
    return {
        client: Array.isArray(clientArray) ? clientArray : [],
        status: Array.isArray(statusArray) ? statusArray : [],
        priority: Array.isArray(priorityArray) ? priorityArray : [],
        technician: Array.isArray(technicianArray) ? technicianArray : [],
        department: Array.isArray(departmentArray) ? departmentArray : [],
        searchTerm: String(routeQuery.searchTerm || ''),
        startDate: String(routeQuery.startDate || ''),
        endDate: String(routeQuery.endDate || ''),
        includeDeleted: routeQuery.includeDeleted === 'true',
        page: routeQuery.page ? parseInt(String(routeQuery.page)) : 0,
        size: routeQuery.size ? parseInt(String(routeQuery.size)) : 25,
        sort: parseSortFromUrl(routeQuery.sort as string)
    };
};

// Build URL params from filter object
const buildUrlFromFilter = (filter: RequestsListFilter): string => {
    const params = new URLSearchParams();
    
    if (filter.client && filter.client.length > 0) params.set('client', filter.client.join(','));
    if (filter.status && filter.status.length > 0) params.set('status', filter.status.join(','));
    if (filter.priority && filter.priority.length > 0) params.set('priority', filter.priority.join(','));
    if (filter.technician && filter.technician.length > 0) params.set('technician', filter.technician.join(','));
    if (filter.department && filter.department.length > 0) params.set('department', filter.department.join(','));
    if (filter.searchTerm) params.set('searchTerm', filter.searchTerm);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    if (filter.includeDeleted) params.set('includeDeleted', 'true');
    if (filter.page && filter.page > 0) params.set('page', filter.page.toString());
    if (filter.size && filter.size !== 25) params.set('size', filter.size.toString());
    
    // Add sort parameter
    if (filter.sort && filter.sort.length > 0) {
        const sortString = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        }).join(',');
        params.set('sort', sortString);
    }
    
    return params.toString();
};

// Helper to check if saved filters have any non-empty values
const hasNonEmptyFilterValues = (filters: any): boolean => {
    if (!filters) return false;
    
    if (Array.isArray(filters.client) && filters.client.length > 0) return true;
    if (Array.isArray(filters.status) && filters.status.length > 0) return true;
    if (Array.isArray(filters.priority) && filters.priority.length > 0) return true;
    if (filters.startDate && filters.startDate.trim() !== '') return true;
    if (filters.endDate && filters.endDate.trim() !== '') return true;
    if (filters.includeDeleted === true) return true;
    
    return false;
};

// Get default filter values
const getDefaultFilter = (): RequestsListFilter => ({
    client: [],
    status: [],
    priority: [],
    technician: [],
    department: [],
    searchTerm: '',
    startDate: '',
    endDate: '',
    includeDeleted: false,
    page: 0,
    size: 25,
    sort: []
});

/**
 * Initialize filter on component mount:
 * 1. If URL has params → use those (for bookmarks/shared links)
 * 2. If URL is blank → load saved filters from database
 *    a. If saved filters exist → use them and update URL
 *    b. If no saved filters → use defaults
 * 
 * After initialization, ONLY filter controls update currentFilter.
 * The URL is just a reflection of the filter state.
 */
const initializeFilter = async (): Promise<void> => {
    // In embedded mode, just use defaults - don't interact with URL or saved filters
    if (props.embedded || props.requestId) {
        currentFilter.value = getDefaultFilter();
        console.debug('Embedded mode - using default filter');
        return;
    }
    
    const hasUrlParams = window.location.search.length > 0;
    
    if (hasUrlParams) {
        // URL has params (bookmark/shared link) - use them and save to DB
        currentFilter.value = parseUrlToFilter();
        console.debug('Initialized from URL params:', currentFilter.value);
        
        // Save these as the user's new filter preferences
        Cache.set('requests-list-filter', currentFilter.value);
        saveDatatableFilters(currentFilter.value);
    } else {
        // Blank URL - try to load saved filters from database
        const dbSettings = await loadDatatableSettings();
        
        if (dbSettings?.filters && hasNonEmptyFilterValues(dbSettings.filters)) {
            // Use saved filters
            currentFilter.value = {
                client: dbSettings.filters.client || [],
                status: dbSettings.filters.status || [],
                priority: dbSettings.filters.priority || [],
                technician: dbSettings.filters.technician || [],
                department: dbSettings.filters.department || [],
                searchTerm: dbSettings.filters.searchTerm || '',
                startDate: dbSettings.filters.startDate || '',
                endDate: dbSettings.filters.endDate || '',
                includeDeleted: dbSettings.filters.includeDeleted || false,
                page: 0,
                size: dbSettings.filters.size || 25,
                sort: dbSettings.filters.sort || []
            };
            console.debug('Initialized from saved filters:', currentFilter.value);
        } else {
            // No saved filters - use defaults
            currentFilter.value = getDefaultFilter();
            console.debug('Initialized with defaults');
        }
        
        // Update URL to reflect current filter state (skip in embedded mode or if not on request route)
        const isOnRequestRoute = route.path.startsWith('/request');
        if (!props.embedded && !props.requestId && isOnRequestRoute) {
            const urlParams = buildUrlFromFilter(currentFilter.value);
            const newUrl = `/request/list${urlParams ? '?' + urlParams : ''}`;
            router.replace(newUrl);
        }
    }
};

// setFilter - called by filter controls to update filter state
// This is the ONLY place (besides init) that updates currentFilter
const setFilter = (filterUpdate: any) => {
    console.log('RequestsDatatable - setFilter called with:', filterUpdate);
    
    const tempFilter = { ...currentFilter.value };
    
    if (filterUpdate.client !== undefined) {
        tempFilter.client = Array.isArray(filterUpdate.client) ? filterUpdate.client : (filterUpdate.client ? [filterUpdate.client] : []);
    }
    if (filterUpdate.status !== undefined) {
        tempFilter.status = Array.isArray(filterUpdate.status) ? filterUpdate.status : (filterUpdate.status ? [filterUpdate.status] : []);
    }
    if (filterUpdate.priority !== undefined) {
        tempFilter.priority = Array.isArray(filterUpdate.priority) ? filterUpdate.priority : (filterUpdate.priority ? [filterUpdate.priority] : []);
    }
    if (filterUpdate.technician !== undefined) {
        tempFilter.technician = Array.isArray(filterUpdate.technician) ? filterUpdate.technician : (filterUpdate.technician ? [filterUpdate.technician] : []);
    }
    if (filterUpdate.department !== undefined) {
        tempFilter.department = Array.isArray(filterUpdate.department) ? filterUpdate.department : (filterUpdate.department ? [filterUpdate.department] : []);
    }
    if (filterUpdate.startDate !== undefined) tempFilter.startDate = filterUpdate.startDate || '';
    if (filterUpdate.endDate !== undefined) tempFilter.endDate = filterUpdate.endDate || '';
    if (filterUpdate.includeDeleted !== undefined) tempFilter.includeDeleted = filterUpdate.includeDeleted;
    
    tempFilter.page = 0;
    
    // 1. Update currentFilter
    currentFilter.value = tempFilter;
    
    // 2. Save to storage
    Cache.set('requests-list-filter', currentFilter.value);
    saveDatatableFilters(currentFilter.value);
    
    // 3. Update URL to reflect new filter state (skip in embedded mode or if not on request route)
    const isOnRequestRoute = route.path.startsWith('/request');
    if (!props.embedded && !props.requestId && isOnRequestRoute) {
        const urlParams = buildUrlFromFilter(tempFilter);
        const newUrl = `/request/list${urlParams ? '?' + urlParams : ''}`;
        console.log('setFilter updating URL to:', newUrl);
        router.replace(newUrl);
    }
    
    // 4. Reload data with new filter
    refresh();
};

const refresh = () => {
    Emitter.emit('datatable-refresh', 'requests-list');
};

// State for embedded edit modal
const showEditModal = ref(false);
const editingRecordId = ref<number | null>(null);
const isNewRecord = ref(false);

const newRecord = () => {
    if (props.embedded || props.requestId) {
        // In embedded mode, show modal instead of navigating
        editingRecordId.value = null;
        isNewRecord.value = true;
        showEditModal.value = true;
    } else {
        router.push({
            path: "/request/edit/new"
        });
    }
};

const editRecord = (id: any) => {
    if (props.embedded || props.requestId) {
        // In embedded mode, show modal instead of navigating
        editingRecordId.value = id;
        isNewRecord.value = false;
        showEditModal.value = true;
    } else {
        router.push({
            path: "/request/edit/" + id
        });
    }
};

const closeEditModal = () => {
    showEditModal.value = false;
    editingRecordId.value = null;
    isNewRecord.value = false;
};

const onRecordSaved = () => {
    closeEditModal();
    refresh();
};

const editContact = (id?: number) => {
    if (id) {
        Emitter.emit('open:clientForm', id);
    }
};

const openJobForm = (id?: number) => {
    if (id) {
        Emitter.emit('edit:job', id);
    }
};

const deleteRecord = async (id: number) => {
    actionDropdown.value = {};
    
    Alert.confirm('Are you sure you want to delete this request?', async () => {
        try {
            const success = await Api.deleteWorkOrder(id);
            if (success) {
                Toast.success('Request deleted successfully');
                refresh();
            } else {
                Alert.notify('Failed to delete request');
            }
        } catch (error) {
            console.error('Error deleting request:', error);
            Alert.notify('Error deleting request');
        }
    }, () => {});
};

const getStatusLabel = (status?: number | null): string => {
    const found = statusOptions.find(opt => opt.value === status);
    return found?.label || 'Unknown';
};

const getStatusBadgeClass = (status?: number | null): string => {
    switch (status) {
        case 0: return 'bg-secondary';
        case 1: return 'bg-warning';
        case 2: return 'bg-info';
        case 3: return 'bg-success';
        case 4: return 'bg-purple';
        case 5: return 'bg-primary';
        default: return 'bg-secondary';
    }
};

const getPriorityBadgeClass = (priority?: string | null): string => {
    switch (priority?.toLowerCase()) {
        case 'critical': return 'bg-danger';
        case 'high': return 'bg-orange';
        case 'medium': return 'bg-warning text-dark';
        case 'low': return 'bg-info';
        case 'cancelled': return 'bg-purple';
        default: return 'bg-secondary';
    }
};

const getPriorityIcon = (priority?: string | null) => {
    switch (priority?.toLowerCase()) {
        case 'critical': return AlertCircle;
        case 'high': return AlertTriangle;
        case 'medium': return AlertTriangle;
        case 'low': return Info;
        case 'cancelled': return CircleX;
        default: return Circle;
    }
};

const getRowClass = (row: any): string => {
    // Check if row is deleted first
    if (row.IsDeleted) {
        return 'row-deleted';
    }
    
    const status = row.Status;
    switch (status) {
        case 0: return 'row-status-draft';
        case 1: return 'row-status-pending';
        case 2: return 'row-status-approved';
        case 3: return 'row-status-complete';
        case 4: return 'row-status-cancelled';
        case 5: return 'row-status-finalized';
        default: return '';
    }
};

// ===== FILTER BADGE FUNCTIONS =====

const hasActiveFilters = computed(() => {
    return currentFilter.value.client.length > 0 ||
           currentFilter.value.status.length > 0 ||
           currentFilter.value.priority.length > 0 ||
           currentFilter.value.technician.length > 0 ||
           currentFilter.value.department.length > 0 ||
           currentFilter.value.startDate ||
           currentFilter.value.endDate ||
           currentFilter.value.includeDeleted;
});

const getClientName = (customerId: number) => {
    const client = ClientList.value.find((c: any) => c.CustomerNumber === customerId);
    return client?.Company || `Client #${customerId}`;
};

const getStatusName = (statusId: number) => {
    return getStatusLabel(statusId);
};

const removeClientFilter = (clientId: number) => {
    const updatedClients = currentFilter.value.client.filter(c => c !== clientId);
    setFilter({ client: updatedClients });
};

const removeStatusFilter = (statusId: number) => {
    const updatedStatuses = currentFilter.value.status.filter(s => s !== statusId);
    setFilter({ status: updatedStatuses });
};

const removePriorityFilter = (priority: string) => {
    const updatedPriorities = currentFilter.value.priority.filter(p => p !== priority);
    setFilter({ priority: updatedPriorities });
};

const getTechName = (techId: number) => {
    const tech = techStore.list[techId];
    return tech?.TechnicianName || `Tech #${techId}`;
};

const removeTechnicianFilter = (techId: number) => {
    const updated = currentFilter.value.technician.filter(t => t !== techId);
    setFilter({ technician: updated });
};

const removeDepartmentFilter = (dept: string) => {
    const updated = currentFilter.value.department.filter(d => d !== dept);
    setFilter({ department: updated });
};

const removeDateFilter = () => {
    setFilter({ startDate: '', endDate: '' });
};

const clearAllFilters = () => {
    setFilter({
        client: [],
        status: [],
        priority: [],
        technician: [],
        department: [],
        startDate: '',
        endDate: ''
    });
};

const formatDateRange = () => {
    if (currentFilter.value.startDate && currentFilter.value.endDate) {
        return `${currentFilter.value.startDate} to ${currentFilter.value.endDate}`;
    } else if (currentFilter.value.startDate) {
        return `From ${currentFilter.value.startDate}`;
    } else if (currentFilter.value.endDate) {
        return `Until ${currentFilter.value.endDate}`;
    }
    return '';
};

// ===== SELECTION MANAGEMENT =====

const toggleRowSelection = (rowId: number) => {
    const index = selectedRows.value.indexOf(rowId);
    if (index > -1) {
        selectedRows.value.splice(index, 1);
    } else {
        selectedRows.value.push(rowId);
    }
    updateSelectAllState();
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
        selectAll.value = false;
    } else {
        selectedRows.value = rows.value.map((row: any) => row.Id);
        selectAll.value = true;
    }
};

const updateSelectAllState = () => {
    if (rows.value.length === 0) {
        selectAll.value = false;
        return;
    }
    const allSelected = rows.value.every((row: any) => selectedRows.value.includes(row.Id));
    selectAll.value = allSelected;
};

const clearSelection = () => {
    selectedRows.value = [];
    selectAll.value = false;
};

// ===== BULK ACTIONS =====

// Check if any selected rows are deleted (status -1 or IsDeleted)
const hasDeletedRowsSelected = computed(() => {
    if (selectedRows.value.length === 0) return false;
    return selectedRows.value.some(id => {
        const row = rows.value.find(r => r.Id === id);
        return row && (row.Status === -1 || row.IsDeleted);
    });
});

const confirmBulkDelete = async () => {
    if (selectedRows.value.length === 0) return;
    
    Alert.confirm(`Are you sure you want to delete ${selectedRows.value.length} request(s)?`, async () => {
        try {
            for (const id of selectedRows.value) {
                await Api.deleteWorkOrder(id);
            }
            Toast.success(`Successfully deleted ${selectedRows.value.length} request(s)`);
            clearSelection();
            refresh();
        } catch (error) {
            console.error('Error deleting requests:', error);
            Alert.notify({ text: 'Failed to delete some requests', type: 'error' });
        }
    }, () => {});
};

const confirmBulkRestore = async () => {
    if (selectedRows.value.length === 0) return;
    
    Alert.confirm(`Are you sure you want to restore ${selectedRows.value.length} request(s)?`, async () => {
        try {
            for (const id of selectedRows.value) {
                await Api.restoreWorkOrder(id);
            }
            Toast.success(`Successfully restored ${selectedRows.value.length} request(s)`);
            clearSelection();
            refresh();
        } catch (error) {
            console.error('Error restoring requests:', error);
            Alert.notify({ text: 'Failed to restore some requests', type: 'error' });
        }
    }, () => {});
};

// Direct data loading function
const loadData = async () => {
    console.debug('loadData called with currentFilter:', currentFilter.value);
    
    await handleDatatableUpdate({
        page: currentFilter.value.page,
        size: currentFilter.value.size,
        sort: currentFilter.value.sort
    });
};

// Watch route path changes - only reload data when returning to list route
// NOTE: Filter controls are the ONLY thing that updates currentFilter
// The URL is just a reflection of the filter state for bookmarking/sharing
watch(() => route.path, async (newPath, oldPath) => {
    // Skip route watching in embedded mode
    if (props.embedded || props.requestId) return;
    
    // Only process if we're actually on a request route
    if (!newPath.startsWith('/request')) return;
    
    // When navigating back to the list route from another route (e.g., edit modal)
    if ((newPath.endsWith('/list') || newPath === '/request') && 
        oldPath && !oldPath.endsWith('/list') && oldPath !== '/request') {
        console.debug('Returned to list route, reloading with current filter');
        
        // Update URL to reflect current filter state (in case it was lost)
        const urlParams = buildUrlFromFilter(currentFilter.value);
        const expectedUrl = `/request/list${urlParams ? '?' + urlParams : ''}`;
        
        if (window.location.pathname + window.location.search !== expectedUrl) {
            router.replace(expectedUrl);
        }
        
        refresh();
    }
}, { immediate: false });

// Listen for draft filter events from RequestsHeader badges
let unsubDraftFilter: Function | null = null;

const handleDraftFilter = (type: string) => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    oneWeekAgo.setHours(0, 0, 0, 0);
    const oneWeekAgoStr = oneWeekAgo.toISOString().split('T')[0];

    if (type === 'recent') {
        // Draft status + created within last 7 days
        setFilter({
            status: [0],
            startDate: oneWeekAgoStr,
            endDate: '',
            client: [],
            priority: [],
            technician: [],
            department: [],
            includeDeleted: false
        });
    } else if (type === 'stale') {
        // Draft status + created more than 7 days ago
        setFilter({
            status: [0],
            startDate: '',
            endDate: oneWeekAgoStr,
            client: [],
            priority: [],
            technician: [],
            department: [],
            includeDeleted: false
        });
    }
};

onMounted(async () => {
    console.debug('RequestsDatatable mounted - embedded:', props.embedded, 'requestId:', props.requestId, 'clientId:', props.clientId);
    unsubDraftFilter = Emitter.on('request:filter-drafts', handleDraftFilter);
    await initializeFilter();
    // Always refresh after initialization - currentFilter is now set correctly
    refresh();
});

onUnmounted(() => {
    if (unsubDraftFilter) unsubDraftFilter();
});
</script>

<style scoped>

.bg-purple {
    background-color: purple;
}

:deep(td a) {
    text-decoration: none;
    color: #333;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid transparent;
    transition: all 0.2s ease-in-out;
    display: inline-block;
    position: relative;
}

:deep(td a:hover) {
    background-color: #15486A;
    color: #fff;
}

:deep(td a:hover::after) {
    content: '\f044';
    font-family: 'Font Awesome 5 Free';
    font-weight: 900;
    position: absolute;
    top: -6px;
    right: -6px;
    background: #3B9796;
    color: white;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    border: 2px solid white;
}
</style>
